/**
 * Presentation labels for a stored Google Drive reference.
 *
 * Migration 072 stores three provenance columns on `lms_material`. Only two of
 * them may ever reach a rendered surface: the MIME type, which says what the
 * resource IS, and the fact that Drive is where it lives. The file ID and the
 * resource key are deliberately absent from this module's inputs — the resource
 * key is link-sharing material, and a file ID in visible text invites someone to
 * treat it as an address.
 *
 * Nothing here asserts that a reader can OPEN the file. Google owns the ACL, and
 * IMAM stores a reference; the copy this module feeds must stay on the record
 * side of that line.
 */

/** Where a picker-sourced material lives. Never rendered for a manual link. */
export const GOOGLE_DRIVE_SOURCE_LABEL = "Google Drive";

/**
 * The generic answer for a supported-but-unlabelled MIME type.
 *
 * A Drive file whose type this map does not name is still a Drive file, and
 * saying so beats both silence and a raw `application/vnd.…` string. It is the
 * fallback rather than the default: an unknown type is not an error.
 */
export const GOOGLE_DRIVE_GENERIC_TYPE_LABEL = "Google Drive file";

/**
 * Friendly names for the types this slice actually expects.
 *
 * Picker returns the Google-native editor types for Docs/Sheets/Slides and the
 * ordinary IANA type for an uploaded file. Both shapes appear here because both
 * are reachable through a `DOCS` view.
 */
const MIME_TYPE_LABELS: Record<string, string> = {
  "application/pdf": "PDF",
  "application/vnd.google-apps.document": "Google Doc",
  "application/vnd.google-apps.spreadsheet": "Google Sheet",
  "application/vnd.google-apps.presentation": "Google Slides",
  "application/vnd.google-apps.drawing": "Google Drawing",
  "application/vnd.google-apps.form": "Google Form",
  "application/vnd.google-apps.script": "Google Apps Script",
  "application/vnd.google-apps.jam": "Google Jamboard",
  "application/vnd.google-apps.site": "Google Site",
  "application/msword": "Word document",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "Word document",
  "application/vnd.ms-excel": "Excel workbook",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
    "Excel workbook",
  "application/vnd.ms-powerpoint": "PowerPoint deck",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    "PowerPoint deck",
  "text/plain": "Text file",
  "text/csv": "CSV file",
  "image/jpeg": "JPEG image",
  "image/png": "PNG image",
  "video/mp4": "MP4 video",
  "audio/mpeg": "MP3 audio",
};

/**
 * The human label for a stored Drive MIME type, or null when there is no Drive
 * mapping at all.
 *
 * Null means "this is not a picker-sourced material" and is the signal callers
 * use to keep manual links rendering exactly as they did before this feature.
 * A present-but-unrecognized type returns the generic label rather than null,
 * because the material IS on Drive whether or not this map has heard of its
 * type — returning null there would erase real provenance.
 */
export function googleDriveTypeLabel(mimeType: string | null | undefined): string | null {
  if (typeof mimeType !== "string") return null;
  const normalized = mimeType.trim().toLowerCase();
  if (!normalized) return null;
  return MIME_TYPE_LABELS[normalized] ?? GOOGLE_DRIVE_GENERIC_TYPE_LABEL;
}

/**
 * The accessible name for a link that leaves IMAM for Drive.
 *
 * Screen-reader users navigating by link list get the title and nothing else
 * from the visible text, so the destination is folded into the name here. It
 * states where the link GOES, never that the reader may open it.
 */
export function googleDriveLinkAccessibleName(title: string): string {
  return `${title} (opens in Google Drive)`;
}

/** The hosts a Google Drive file is addressed on. */
const GOOGLE_DRIVE_HOSTS = new Set(["drive.google.com", "docs.google.com"]);

/**
 * Whether an address is a Google Drive file's, by its host alone. For a link
 * IMAM holds without Drive provenance (a learner's hand-in): the address is
 * the only fact, and the host, not the path, says Google holds the file.
 */
export function isGoogleDriveAddress(url: string | null | undefined): boolean {
  if (typeof url !== "string") return false;
  try {
    const { protocol, hostname } = new URL(url.trim());
    return (protocol === "https:" || protocol === "http:") && GOOGLE_DRIVE_HOSTS.has(hostname.toLowerCase());
  } catch {
    return false;
  }
}

/** Which Drive glyph an item takes: its own kind's, or Drive's for any other. */
export type GoogleDriveMarkKind = "doc" | "sheet" | "slides" | "pdf" | "drive";

const MARK_BY_MIME_TYPE: Record<string, GoogleDriveMarkKind> = {
  "application/vnd.google-apps.document": "doc",
  "application/vnd.google-apps.spreadsheet": "sheet",
  "application/vnd.google-apps.presentation": "slides",
  "application/pdf": "pdf",
};

/** A Google editor's address names its kind in the first path segment. */
const MARK_BY_EDITOR_PATH: Record<string, GoogleDriveMarkKind> = {
  document: "doc",
  spreadsheets: "sheet",
  presentation: "slides",
};

/**
 * The glyph for a Drive item, from the stored MIME type when the reference
 * recorded one, else from a Google editor address (`docs.google.com/document/…`).
 * Anything else on Drive, a `drive.google.com/file/…` address included, takes
 * the Drive mark: a guess at its kind would be a claim IMAM never stored.
 */
export function googleDriveMarkKind(mimeType: string | null | undefined, url?: string | null): GoogleDriveMarkKind {
  const normalized = typeof mimeType === "string" ? mimeType.trim().toLowerCase() : "";
  if (normalized) return MARK_BY_MIME_TYPE[normalized] ?? "drive";
  if (!url) return "drive";
  try {
    const parsed = new URL(url.trim());
    if (parsed.hostname.toLowerCase() !== "docs.google.com") return "drive";
    return MARK_BY_EDITOR_PATH[parsed.pathname.split("/")[1] ?? ""] ?? "drive";
  } catch {
    return "drive";
  }
}
